// src/widgets/PlayerRunModal.jsx
import { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Select from 'react-select'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { th } from 'date-fns/locale/th'
import {
  Gamepad2, X, Layers, CalendarClock, CalendarRange, Hash, Infinity, Target,
  Eye, EyeOff, BarChart3, Clock, Smartphone, Monitor, Trophy, Loader
} from 'lucide-react'
import {
  getTestList,
  getTestRunDetail,
  createPlayerRun,
  updatePlayerRun
} from '@/api/kuizy'
import ConfirmModal from '@/widgets/ConfirmModal'
import { formatDateTime } from '@/settings/master'

registerLocale('th', th)

const solutionLabel = {
  question: 'เฉพาะคำถาม',
  full: 'คำถาม + เฉลย',
  none: 'ไม่แสดง'
}

const two = n => String(n).padStart(2, '0')
/** ส่งเวลาเป็น local-time (ไม่มี Z) ป้องกันเวลาเลื่อน */
const toSqlLocalNoTZ = d => (
  `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}`
)
/** parse "YYYY-MM-DDTHH:mm:ss" (ไม่มีโซนเวลา) เป็น Date local */
const parseLocal = s => {
  if (!s) return null
  const m = String(s).trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/)
  if (!m) {
    const d = new Date(s)
    return isNaN(d.getTime()) ? null : d
  }
  const [, Y, M, D, h, m2, s2] = m
  return new Date(Number(Y), Number(M) - 1, Number(D), Number(h), Number(m2), Number(s2 || 0), 0)
}

/**
 * PlayerRunModal
 * props:
 * - open: boolean
 * - mode: 'create' | 'edit'
 * - runId?: string (required if mode='edit')
 * - testId?: string (ถ้าส่งมา -> ซ่อนเลือกชุดข้อสอบ)
 * - testMeta?: object (optional header info {test_id, test_name, content_name, criteria, duration})
 * - onClose: () => void
 * - onSuccess: ({ ok, mode, run_id? }) => void
 */
export default function PlayerRunModal({
  open,
  mode = 'create',
  runId = null,
  testId = null,
  testMeta = null,
  onClose,
  onSuccess
}) {
  const isEdit = mode === 'edit'
  const orgId = (typeof window !== 'undefined' && localStorage.getItem('orgId')) || ''
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [tests, setTests] = useState([])
  const [selectedTest, setSelectedTest] = useState(null)

  const [attemptRule, setAttemptRule] = useState('unlimited')
  const [attemptLimit, setAttemptLimit] = useState(1)
  const [showSolution, setShowSolution] = useState('question')
  const [showRanking, setShowRanking] = useState(true)
  const [platformWeb, setPlatformWeb] = useState(true)
  const [platformMobile, setPlatformMobile] = useState(true)

  const [startAt, setStartAt] = useState(null)
  const [endAt, setEndAt] = useState(null)
  const [dateErr, setDateErr] = useState('')

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmPending, setConfirmPending] = useState(false)

  const testOptions = useMemo(() => {
    if (!Array.isArray(tests)) return []
    return tests.filter(t => t && t.test_id).map(t => ({
      value: t.test_id,
      label: `${t.content_name ? `${t.content_name}` : ''} • ${t.test_name}`,
      data: t
    }))
  }, [tests])

  const headerMeta = useMemo(() => {
    if (selectedTest?.data) return selectedTest.data
    if (testMeta) return testMeta
    return null
  }, [selectedTest, testMeta])

  const validateDates = () => {
    if (!startAt || !endAt) { setDateErr('กรุณาเลือกวันเวลาเริ่ม และสิ้นสุด'); return false }
    if (endAt <= startAt) { setDateErr('วันสิ้นสุดต้องช้ากว่าเวลาเริ่มอย่างน้อย 1 นาที'); return false }
    setDateErr('')
    return true
  }

  // โหลดข้อมูลสำหรับแก้ไข หรือเตรียมค่าตั้งต้น
  useEffect(() => {
    if (!open) return
    let alive = true
    const run = async () => {
      setLoading(true)
      try {
        let arr = []
        if (!testId) {
          const { data } = await getTestList('active')
          arr = Array.isArray(data) ? data.filter(x => x && x.test_id) : []
          if (!alive) return
          setTests(arr)
        }
        if (isEdit && runId) {
          const { data } = await getTestRunDetail({ org_id: orgId, run_id: runId })
          if (!alive) return
          const payload = data || {}
          const s = payload.settings || {}
          const ttest = payload.test || {}
          const run = payload.run || {}

          // map settings (camelCase) → state (fallback ไป snake_case เดิม)
          const rule = s.attemptRule ?? payload.attempt_rule ?? 'unlimited'
          setAttemptRule(rule)
          setAttemptLimit(rule === 'limited' ? (s.attemptLimit ?? payload.attempt_limit ?? 1) : 1)
          setShowSolution(s.showSolution ?? payload.show_solution ?? 'question')
          setShowRanking(typeof s.showRanking === 'boolean' ? s.showRanking : Boolean(payload.show_ranking))
          const plat = s.platform ?? payload.platform ?? 'all'
          setPlatformWeb(plat !== 'mobileOnly')
          setPlatformMobile(plat !== 'webOnly')

          // เวลา: ให้ความสำคัญ run > test > flat
          const st = run.start_time || ttest.start_time || payload.start_time
          const et = run.end_time || ttest.end_time || payload.end_time
          setStartAt(parseLocal(st))
          setEndAt(parseLocal(et))
          setDateErr('')

          // เลือกชุดข้อสอบจากรายการ (เฉพาะกรณีไม่ได้ส่ง testId มา)
          const tid = ttest.id ?? payload.test_id
          if (!testId && tid) {
            const list = arr.length ? arr : tests
            const found = Array.isArray(list) ? list.find(x => x.test_id === tid) : null
            if (found) {
              setSelectedTest({
                value: found.test_id,
                label: `${found.content_name || ''} • ${found.test_name}`,
                data: found
              })
            }
          }
        } else {
          // โหมดสร้างใหม่
          setAttemptRule('unlimited')
          setAttemptLimit(1)
          setShowSolution('question')
          setShowRanking(true)
          setPlatformWeb(true)
          setPlatformMobile(true)
          setStartAt(null)
          setEndAt(null)
          setDateErr('')
          if (testId) setSelectedTest(null)
        }
      } finally {
        if (alive) setLoading(false)
      }
    }
    run()
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, runId, testId, orgId])

  const handleConfirm = async () => {
    if (!validateDates()) return
    setConfirmPending(true)
    try {
      const platform = platformWeb && platformMobile ? 'all' : platformWeb ? 'webOnly' : platformMobile ? 'mobileOnly' : 'all'

      if (isEdit) {
        setSaving(true)
        const payload = {
          run_id: runId,
          exam_mode: 'easy',
          attempt_rule: attemptRule,
          show_solution: showSolution,
          show_ranking: showRanking,
          platform,
          start_time: toSqlLocalNoTZ(startAt),
          end_time: toSqlLocalNoTZ(endAt)
        }
        if (attemptRule === 'limited') {
          payload.attempt_limit = attemptLimit
        }
        await updatePlayerRun(payload)
        setSaving(false)
        setConfirmOpen(false)
        setConfirmPending(false)
        onClose?.()
        onSuccess?.({ ok: true, mode: 'edit', run_id: runId })
      } else {
        const idForCreate = testId || selectedTest?.value
        if (!idForCreate) { alert('กรุณาเลือกชุดข้อสอบ'); setConfirmPending(false); return }
        const { run_id } = await createPlayerRun({
          test_id: idForCreate,
          settings: {
            examMode: 'easy',
            attemptRule: attemptRule,
            attemptLimit: attemptRule === 'limited' ? attemptLimit : null,
            showSolution: showSolution,
            showRanking: showRanking,
            platform
          },
          start_time: toSqlLocalNoTZ(startAt),
          end_time: toSqlLocalNoTZ(endAt)
        })
        setConfirmOpen(false)
        setConfirmPending(false)
        onClose?.()
        onSuccess?.({ ok: true, mode: 'create', run_id })
      }
    } catch (e) {
      setSaving(false)
      setConfirmPending(false)
      alert(isEdit ? 'บันทึกการแก้ไขไม่สำเร็จ' : 'สร้างเกมไม่สำเร็จ')
    }
  }

  if (!open) return null

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div initial={{ scale: .96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: .96, opacity: 0 }}
          className="w-full max-w-4xl h-[90vh] rounded-3xl shadow-2xl border border-white/10 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-5 h-full">
            <div className="relative md:col-span-2 bg-gradient-to-br from-secondary/20 via-primary/15 to-purple-100">
              <div className="relative p-6 h-full flex flex-col">
                <div className="flex items-center gap-3">
                  <div className="size-11 rounded-2xl bg-white/70 grid place-items-center">
                    <Gamepad2 className="size-6 text-primary" />
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {isEdit ? 'แก้ไขเกม (ผู้เล่น)' : 'สร้างเกมใหม่ (ผู้เล่น)'}
                  </div>
                </div>

                <div className="mt-5 rounded-2xl bg-white/70 p-4">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Trophy className="size-5 text-amber-500" />
                    <div className="text-sm">ชุดข้อสอบ</div>
                  </div>
                  <div className="mt-1 text-gray-900 font-medium text-sm leading-snug">
                    {headerMeta
                      ? `${headerMeta.content_name || ''} • ${headerMeta.test_name || ''}`
                      : (selectedTest?.label || '-')}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-700">
                    <div className="flex items-center gap-1">
                      <BarChart3 className="size-4" />
                      <span>เกณฑ์ผ่าน</span>
                    </div>
                    <div className="text-right">{headerMeta?.criteria ?? selectedTest?.data?.criteria ?? 80}%</div>
                    <div className="flex items-center gap-1">
                      <Clock className="size-4" />
                      <span>เวลา</span>
                    </div>
                    <div className="text-right">{headerMeta?.duration ?? selectedTest?.data?.duration ?? 0} นาที</div>
                  </div>
                </div>

                <div className="mt-auto pt-6 text-[11px] text-gray-700/80">
                  โหมดผู้เล่นสาธารณะ • แชร์ลิงก์หรือ QR ได้ทันที
                </div>
              </div>
            </div>

            <div className="md:col-span-3 flex flex-col overflow-hidden">
              <div className="flex items-start justify-between">
                <div className="text-base font-semibold text-gray-900 mt-6 ml-6">ตั้งค่าเกมข้อสอบ</div>
                <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100">
                  <X className="size-5 text-gray-500" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-6 overscroll-contain">
                {loading ? (
                  <div className="py-14 grid place-items-center text-gray-600">
                    <Loader className="animate-spin" />
                  </div>
                ) : (
                  <div className="mt-4 grid gap-5">
                    {!testId && (
                      <div>
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                          <Layers className="size-4 text-primary" />
                          เลือกชุดข้อสอบ
                        </div>
                        <div className="mt-2">
                          <Select
                            classNamePrefix="rs"
                            options={testOptions}
                            value={selectedTest}
                            onChange={setSelectedTest}
                            isSearchable
                            isDisabled={isEdit}
                            placeholder={isEdit ? 'ไม่สามารถเปลี่ยนชุดข้อสอบได้' : 'ค้นหาและเลือกชุดข้อสอบ'}
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <CalendarRange className="size-4 text-primary" />
                        ช่วงเวลาเปิดสอบ
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="relative">
                          <CalendarClock className="size-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                          <DatePicker
                            locale="th"
                            selected={startAt}
                            onChange={d => setStartAt(d)}
                            timeInputLabel="เวลา:"
                            timeFormat="HH:mm"
                            showTimeInput
                            dateFormat="dd/MM/yyyy HH:mm"
                            placeholderText="เลือกวัน/เวลาเริ่ม"
                            className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-primary/60"
                            calendarClassName="kuizy-dp"
                            popperClassName="z-[2000]"
                            withPortal={isMobile}
                          />
                        </div>
                        <div className="relative">
                          <CalendarClock className="size-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                          <DatePicker
                            locale="th"
                            selected={endAt}
                            onChange={d => setEndAt(d)}
                            timeInputLabel="เวลา:"
                            timeFormat="HH:mm"
                            showTimeInput
                            dateFormat="dd/MM/yyyy HH:mm"
                            placeholderText="เลือกวัน/เวลาสิ้นสุด"
                            className="w-full border border-gray-300 rounded-xl pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-primary/60"
                            minDate={startAt || undefined}
                            calendarClassName="kuizy-dp"
                            popperClassName="z-[2000]"
                            withPortal={isMobile}
                          />
                        </div>
                      </div>
                      {dateErr ? <div className="mt-2 text-xs text-red-600">{dateErr}</div> : null}
                    </div>

                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <Hash className="size-4 text-primary" />
                        จำนวนครั้งที่สอบได้
                      </div>
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        <button
                          onClick={() => setAttemptRule('unlimited')}
                          className={`h-10 rounded-xl border text-sm flex items-center justify-center gap-2 ${attemptRule === 'unlimited' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 text-gray-700'}`}>
                          <Infinity className="size-4" /> ไม่จำกัด
                        </button>
                        <button
                          onClick={() => setAttemptRule('untilPass')}
                          className={`h-10 rounded-xl border text-sm flex items-center justify-center gap-2 ${attemptRule === 'untilPass' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 text-gray-700'}`}>
                          <Target className="size-4" /> จนกว่าจะผ่าน
                        </button>
                        <div className={`h-10 rounded-xl border text-sm flex items-center justify-between px-2 ${attemptRule === 'limited' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 text-gray-700'}`}>
                          <button onClick={() => setAttemptRule('limited')} className="flex items-center gap-2">
                            <Hash className="size-4" /> กำหนดครั้ง
                          </button>
                          {attemptRule === 'limited' && (
                            <input
                              type="number" min={1} value={attemptLimit}
                              onChange={e => setAttemptLimit(Math.max(1, Number(e.target.value || 1)))}
                              className="h-8 w-16 text-center border rounded-lg"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <Eye className="size-4 text-primary" />
                        หลังสอบเสร็จต้องการแสดง
                      </div>
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        <button
                          onClick={() => setShowSolution('question')}
                          className={`h-10 rounded-xl border text-sm flex items-center justify-center gap-2 ${showSolution === 'question' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 text-gray-700'}`}>
                          <Eye className="size-4" /> เฉพาะคำถาม
                        </button>
                        <button
                          onClick={() => setShowSolution('full')}
                          className={`h-10 rounded-xl border text-sm flex items-center justify-center gap-2 ${showSolution === 'full' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 text-gray-700'}`}>
                          <Eye className="size-4" /> คำถาม + เฉลย
                        </button>
                        <button
                          onClick={() => setShowSolution('none')}
                          className={`h-10 rounded-xl border text-sm flex items-center justify-center gap-2 ${showSolution === 'none' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 text-gray-700'}`}>
                          <EyeOff className="size-4" /> ไม่แสดง
                        </button>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="flex items-center gap-2 h-11 px-3 rounded-xl border border-gray-300">
                        <input type="checkbox" checked={showRanking} onChange={e => setShowRanking(e.target.checked)} />
                        <span className="flex items-center gap-2 text-sm text-gray-800">
                          <Trophy className="size-4 text-amber-500" /> แสดงตารางคะแนน
                        </span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className={`h-11 px-3 rounded-xl border flex items-center gap-2 ${platformWeb ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 text-gray-800'}`}>
                          <input type="checkbox" checked={platformWeb} onChange={e => setPlatformWeb(e.target.checked)} />
                          <Monitor className="size-4" /> Web
                        </label>
                        <label className={`h-11 px-3 rounded-xl border flex items-center gap-2 ${platformMobile ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 text-gray-800'}`}>
                          <input type="checkbox" checked={platformMobile} onChange={e => setPlatformMobile(e.target.checked)} />
                          <Smartphone className="size-4" /> Mobile
                        </label>
                      </div>
                    </div>

                    {/* สรุปค่าปัจจุบันเพื่อให้เห็นว่าเคยเลือกอะไรไว้ */}
                    <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700">
                      <div>ช่วงเวลา: {startAt ? formatDateTime(startAt) : '-'} ถึง {endAt ? formatDateTime(endAt) : '-'}</div>
                      <div>จำนวนครั้ง: {attemptRule === 'limited' ? `จำกัด ${attemptLimit} ครั้ง` : (attemptRule === 'untilPass' ? 'จนกว่าจะผ่าน' : 'ไม่จำกัด')}</div>
                      <div>หลังสอบเสร็จ: {solutionLabel[showSolution]}</div>
                      <div>ตารางคะแนน: {showRanking ? 'แสดง' : 'ไม่แสดง'}</div>
                      <div>แพลตฟอร์ม: {(platformWeb && platformMobile) ? 'Web + Mobile' : (platformMobile ? 'Mobile' : 'Web')}</div>
                    </div>
                  </div>
                )}

                <div className="border-t px-5 md:px-6 py-4 flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="h-10 px-4 rounded-xl border border-gray-300 text-gray-700"
                  >
                    ยกเลิก
                  </button>
                  <button
                    onClick={() => setConfirmOpen(true)}
                    disabled={
                      saving ||
                      loading ||
                      !startAt ||
                      !endAt ||
                      endAt <= startAt ||
                      (!isEdit && !testId && !selectedTest?.value)
                    }
                    className="h-10 px-5 rounded-xl bg-primary text-white disabled:opacity-60"
                  >
                    {isEdit ? (saving ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข') : 'ดำเนินการต่อ'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {confirmOpen && (
          <ConfirmModal
            type="confirm"
            title={isEdit ? 'ยืนยันบันทึกการแก้ไข' : 'ยืนยันสร้างเกม (ผู้เล่น)'}
            message={
              `ชุดข้อสอบ: “${headerMeta?.test_name || selectedTest?.data?.test_name || ''}”\n` +
              `ช่วงเวลา: ${startAt ? formatDateTime(startAt) : '-'} ถึง ${endAt ? formatDateTime(endAt) : '-'}\n` +
              `จำนวนครั้ง: ${attemptRule === 'limited' ? `จำกัด ${attemptLimit} ครั้ง` : (attemptRule === 'untilPass' ? 'จนกว่าจะผ่าน' : 'ไม่จำกัด')}\n` +
              `หลังสอบเสร็จ: ${solutionLabel[showSolution]}\n` +
              `ตารางคะแนน: ${showRanking ? 'แสดง' : 'ไม่แสดง'}\n` +
              `แพลตฟอร์ม: ${(platformWeb && platformMobile) ? 'Web + Mobile' : (platformMobile ? 'Mobile' : 'Web')}`
            }
            pending={confirmPending}
            onConfirm={handleConfirm}
            onCancel={() => { setConfirmOpen(false); setConfirmPending(false) }}
          />
        )}
      </motion.div>
    </AnimatePresence>
  )
}
